
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Bell, Menu, LogOut, User, Users, Calendar,
  ChevronRight, Sparkles, X, Check, ExternalLink, Sun, Moon, ShieldCheck
} from "lucide-react";
import API_URL from '../config';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import { isGoldVerified, GoldVerifiedBadge } from './Profile';


// --- Component Helper: Feature Configuration ---
const getFeatures = (role, formationTasks = [], onScheduleClick = null, scheduleDesc = "") => {
  const baseFeatures = [
    {
      name: "Profile",
      link: "/profile",
      icon: <User size={32} />,
      desc: "Manage your academic & professional identity",
      color: "from-blue-500 to-indigo-600"
    }
  ];

  if (role !== 'faculty') {
    baseFeatures.push({
      name: "Daily Schedule",
      onClick: onScheduleClick,
      icon: <Calendar size={32} />,
      desc: scheduleDesc || "View today's classes & room locations",
      color: "from-amber-500 to-orange-600"
    });
  }

  // Add Team Formation tasks if any are active
  // Add Team Formation tasks directly so it's always visible for everyone
  baseFeatures.push({
    name: "Make a Team",
    link: `/formation`, // Link to Hub
    icon: <Users size={32} />,
    desc: "Join or create teams for active events",
    color: "from-violet-600 to-purple-700",
    badge: formationTasks.length > 0 ? "Formation Live" : "Hub"
  });

  baseFeatures.push({
    name: "Events",
    link: `/events`,
    icon: <Sparkles size={32} />,
    desc: "Explore events & network at Convergence 2K26",
    color: "from-pink-500 to-rose-600",
    badge: "Convergence 2K26"
  });

  if (role === 'faculty') {
    baseFeatures.push({
      name: "Explore Students",
      link: "/explore",
      icon: <Users size={32} />,
      desc: "Discover & connect with students",
      color: "from-emerald-500 to-teal-600"
    });
  } else {
    baseFeatures.push({
      name: "Explore",
      link: "/explore",
      icon: <Sparkles size={32} />,
      desc: "Find peers and mentors",
      color: "from-purple-500 to-pink-600"
    });

    baseFeatures.push({
      name: "Attendance",
      link: "/attendance",
      icon: <ShieldCheck size={32} />,
      desc: "Request attendance & track history",
      color: "from-emerald-500 to-teal-600"
    });
  }

  if (role === 'admin' || role === 'faculty') {
    baseFeatures.push({
      name: "Faculty Hub",
      link: "/faculty/home",
      icon: <ShieldCheck size={32} />,
      desc: "Attendance, Announcements & Faculty Tools",
      color: "from-amber-500 to-orange-600"
    });
  }

  return baseFeatures;
};

// --- Component: Suggestions Carousel (Auto + Manual Scroll) ---
// --- Component: Suggestions Carousel (Auto + Manual Scroll) ---
const SuggestionsCarousel = ({ suggestions, handleProfileOpen }) => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  // Duplicate items enough times to ensure smooth infinite scroll
  // We need at least 2 full sets, but 3 or 4 makes the seamless reset easier if screen is wide.
  const seamlessSuggestions = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return [];
    // If list is small, duplicate more times
    const multiplier = suggestions.length < 5 ? 8 : 4;
    return Array(multiplier).fill(suggestions).flat();
  }, [suggestions]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const interval = setInterval(() => {
      if (!isPaused && scrollContainer) {
        scrollContainer.scrollLeft += 1; // Smooth slow scroll

        // Infinite Loop Logic:
        // When we have scrolled past the first full set of items...
        // We instantly jump back to the start (relative to that set).
        // Standard "single width" is roughly: scrollWidth / multiplier
        // But safer is to check if we are near the end.

        // Better trigger: If we are close to the end, jump back to a matching position near start.
        // Let's say we jump back when we are halfway through.
        if (scrollContainer.scrollLeft >= (scrollContainer.scrollWidth / 2)) {
          scrollContainer.scrollLeft = 0;
          // Note: For perfect pixel precision, we'd calculate item widths, 
          // but resetting to 0 when half-way (since we have 4x duplication) 
          // is usually indistinguishable if the sets are identical.
        }
      }
    }, 20); // Slightly faster for smoothness

    return () => clearInterval(interval);
  }, [isPaused, seamlessSuggestions]);

  return (
    <div
      className="suggestions-track-wrapper"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div
        className="suggestions-carousel"
        ref={scrollRef}
      >
        {seamlessSuggestions.map((s, idx) => (
          <div
            // Use index in key because duplications mean IDs aren't unique
            key={`${s.email}-${idx}`}
            className="suggestion-card-modern"
            onClick={() => handleProfileOpen(s.email)}
          >
            <div className="sugg-avatar-large">
              {s.profile_pic ? (
                <img src={s.profile_pic} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (s.name || '?').charAt(0)}
            </div>
            <div className="sugg-info">
              <div className="sugg-name">
                {s.name}
                {isGoldVerified(s) && <GoldVerifiedBadge size={14} style={{ display: 'inline', marginLeft: 4 }} />}
              </div>
              <div className="sugg-dept">{s.year} • {s.department}</div>
              {s._reason && (
                <div style={{
                  fontSize: '0.72rem',
                  fontWeight: '600',
                  color: s._isEvent ? '#eab308' : 'var(--accent-color)',
                  background: s._isEvent ? 'rgba(234, 179, 8, 0.12)' : 'rgba(99, 102, 241, 0.12)',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '10px',
                  marginTop: '0.25rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '160px'
                }}>
                  {s._reason}
                </div>
              )}
              <div className="connect-btn-mini">View Profile</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  // --- State Management ---
  const [user, setUser] = useState({ name: "", email: "" });
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [formationTasks, setFormationTasks] = useState([]);

  // Student Daily Schedule state
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [modalSchedule, setModalSchedule] = useState([]);
  const [defaultRoom, setDefaultRoom] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [currentWeekday, setCurrentWeekday] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [theme, setTheme] = useState(() => localStorage.getItem("vjconnect_theme") || "dark");
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("vjconnect_theme", theme);
  }, [theme]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "vjconnect_theme" && e.newValue) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Push Notification Logic
  useEffect(() => {
    const initPushData = async () => {
      // If already granted, auto-refresh and register the token silently in background
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && user.email) {
        try {
          const { requestForToken } = await import('../firebase');
          const token = await requestForToken();
          if (token) {
            await fetch(`${API_URL}/notifications/register-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: user.email, fcm_token: token, device_info: navigator.userAgent })
            });
          }
        } catch (err) {
          console.error("Auto-token generation failed:", err);
        }
      } 
      // If not decided yet, show the prompt
      else if (typeof Notification !== 'undefined' && Notification.permission === 'default' && user.email) {
        const timer = setTimeout(() => setShowPushPrompt(true), 3000); 
        return () => clearTimeout(timer);
      }
    };
    
    if (user.email) {
      initPushData();
    }
  }, [user.email]);

  const handleEnablePush = async () => {
    try {
      const { requestForToken } = await import('../firebase');
      const token = await requestForToken();
      if (token && user.email) {
        // Register token with backend
        await fetch(`${API_URL}/notifications/register-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: user.email, fcm_token: token, device_info: navigator.userAgent })
        });
      }
      setShowPushPrompt(false);
    } catch (e) {
      console.error(e);
      setShowPushPrompt(false);
    }
  };

  const handleDismissPush = () => {
      setShowPushPrompt(false);
  };

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  // Navigation & Menu
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const menuRef = useRef(null);
  const notifRef = useRef(null);

  // --- Effects: Auth & Data Fetching ---

  // Check Auth
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("vjconnect_user") || "{}");
    if (!userData.name && !userData.email) {
      navigate("/");
    } else if (userData.role === "faculty") {
      navigate("/faculty/home");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  // Log active session visit
  useEffect(() => {
    if (!user.email) return;
    fetch(`${API_URL}/auth/session-ping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: user.email, name: user.name, role: user.role || "student" })
    }).catch(e => console.warn("Failed to ping session", e));
  }, [user.email, user.name, user.role]);

  // Helper: Title Case
  const toUpperName = (s) => {
    if (!s) return s;
    return String(s).trim().replace(/\s+/g, ' ').toUpperCase();
  };

  // Fetch Full Profile for derived data
  useEffect(() => {
    if (!user.email) return;
    fetch(`${API_URL}/student/profile?email=${user.email}`)
      .then(async res => ({ ok: res.ok, body: await res.json() }))
      .then(({ ok, body }) => {
        if (ok && body.exists) {
          const prof = body.profile || {};
          const deriveNameFromEmail = (email) => {
            if (!email) return "";
            const local = String(email).split('@')[0] || "";
            const spaced = local.split(/[._-]/).filter(Boolean).map(p => p).join(' ');
            return toUpperName(spaced || local);
          };

          const updatedUser = {
            ...user,
            profile_pic: prof.profile_pic || user.profile_pic,
            name: toUpperName((prof.name && prof.name.trim()) ? prof.name : (user.name && user.name.trim() ? user.name : deriveNameFromEmail(user.email))),
            rollNo: prof.rollNo || user.rollNo || String(user.email).split('@')[0] || "",
            department: prof.department || user.department,
            year: prof.year || user.year,
            section: prof.section || user.section,
            role: body.role || user.role,
            hobbies: prof.hobbies || user.hobbies,
            aspirations: prof.aspirations || user.aspirations,
            school_name: prof.school_name || user.school_name || "",
            junior_college: prof.junior_college || user.junior_college || "",
            city: prof.city || user.city || "",
          };
          setUser(updatedUser);
          localStorage.setItem("vjconnect_user", JSON.stringify(updatedUser));
        }
      })
      .catch(err => console.error("Failed to fetch profile", err));
  }, [user.email, user.name]); // Minimal dependencies to prevent loops

  // Fetch Students for Search
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoadingStudents(true);
        const response = await fetch(`${API_URL}/users`);
        if (response.ok) {
          const data = await response.json();
          // backend may return either an array, or an object like { users: [...] }
          if (Array.isArray(data)) setStudents(data);
          else if (data && Array.isArray(data.users)) setStudents(data.users);
          else setStudents([]);
        } else {
          setStudents([]);
        }
      } catch (err) {
        console.error("Failed to fetch students", err);
        setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
  }, []);

  // Fetch Formation Tasks
  useEffect(() => {
    if (!user.email) return; // Wait for user to be loaded
    const fetchFormationTasks = async () => {
      try {
        const res = await fetch(`${API_URL}/formation/tasks?email=${user.email}`);
        if (res.ok) {
          const data = await res.json();
          setFormationTasks(data.tasks || []);
        }
      } catch (err) {
        console.error("Failed to fetch formation tasks", err);
      }
    };
    fetchFormationTasks();
  }, [user.email]);

  // Keep track of current time for live period indicator
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Fetch today's schedule for student
  const fetchScheduleForDay = useCallback((dayName) => {
    if (!user.email || user.role === 'faculty') return;
    setLoadingSchedule(true);
    const url = `${API_URL}/timetable/today-student?email=${encodeURIComponent(user.email)}${dayName ? `&day=${dayName}` : ''}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (!dayName) {
          setTodaySchedule(data.schedule || []);
          setModalSchedule(data.schedule || []);
          setDefaultRoom(data.classroom || null);
          setCurrentWeekday(data.today || '');
          setSelectedDay(data.today || '');
        } else {
          setModalSchedule(data.schedule || []);
          setSelectedDay(data.today || '');
        }
      })
      .catch(e => console.error("Error fetching student schedule", e))
      .finally(() => setLoadingSchedule(false));
  }, [user.email, user.role]);

  useEffect(() => {
    fetchScheduleForDay();
  }, [fetchScheduleForDay]);

  // Poll Notifications
  const fetchNotifs = () => {
    if (!user.email) return;
    fetch(`${API_URL}/notifications?email=${user.email}`)
      .then(res => res.json())
      .then(data => setNotifications(data.notifications || []))
      .catch(err => console.error("Notif error", err));
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, [user.email]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle team invitation responses
  const handleInvitationResponse = async (notificationId, accept) => {
    try {
      const notification = notifications.find(n => n.id === notificationId);
      if (!notification) return;

      const data = JSON.parse(notification.data || '{}');
      const invitationId = data.invitation_id;

      const res = await fetch(`${API_URL}/formation/invitations/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ invitation_id: invitationId, accept })
      });

      if (res.ok) {
        // Mark notification as read on backend
        await fetch(`${API_URL}/notifications/mark-read-one`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: notificationId })
        });

        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        if (accept) {
          alert("Invitation accepted! You've joined the team.");
          if (notification.link) {
            navigate(notification.link);
          }
        }
      } else {
        const err = await res.json();
        alert(err.error || "Failed to respond to invitation");
      }
    } catch (err) {
      console.error("Invitation response error:", err);
      alert("Failed to respond to invitation");
    }
  };

  // Handle event connection request responses directly from navbar dropdown
  const handleEventConnectionResponse = async (notification, accept) => {
    try {
      await fetch(`${API_URL}/events-networking/connect/respond-notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notification_id: notification.id,
          accept,
          user_email: user.email,
          user_name: user.name
        })
      });

      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      if (accept) {
        alert("Event connection accepted! 🎉");
      }
    } catch (err) {
      console.error("Event connection response error:", err);
    }
  };

  // --- Logic: Search & Suggestions ---

  // Filter Logic
  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();

    // 1. Get all matches
    const matches = students.filter((student) =>
      (student.name && student.name.toLowerCase().includes(term)) ||
      (student.email && student.email.toLowerCase().includes(term)) ||
      (student.rollNo && student.rollNo.toLowerCase().includes(term)) ||
      (student.department && student.department.toLowerCase().includes(term))
    );

    // 2. Sort: StartsWith > Contains
    return matches.sort((a, b) => {
      const aName = (a.name || "").toLowerCase();
      const bName = (b.name || "").toLowerCase();
      const aRoll = (a.rollNo || "").toLowerCase();
      const bRoll = (b.rollNo || "").toLowerCase();

      const aStarts = aName.startsWith(term) || aRoll.startsWith(term);
      const bStarts = bName.startsWith(term) || bRoll.startsWith(term);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return 0;
    });
  }, [students, searchTerm]);

  // Suggestion Scoring Logic
  const parseListStr = (v) => {
    if (!v) return [];
    try {
      if (typeof v === 'string' && (v.trim().startsWith('[') || v.includes(','))) {
        return v.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      }
      if (Array.isArray(v)) return v.map(s => String(s).trim().toLowerCase()).filter(Boolean);
      return [String(v).trim().toLowerCase()].filter(Boolean);
    } catch (e) { return []; }
  };

  const computeCandidateScore = (candidate, me) => {
    let score = 0;
    const reasons = [];
    if (!candidate || !me) return { score: 0, reasons: [], isEvent: false };

    const cDept = (candidate.department || "").trim().toLowerCase();
    const mDept = (me.department || "").trim().toLowerCase();
    const cYear = (candidate.year || "").trim().toLowerCase();
    const mYear = (me.year || "").trim().toLowerCase();
    const cSec = (candidate.section || "").trim().toLowerCase();
    const mSec = (me.section || "").trim().toLowerCase();

    // Helper to normalize school/college names (e.g. "Triveni School" vs "triveni")
    const normSchoolStr = (str) => {
      if (!str) return "";
      return String(str)
        .toLowerCase()
        .replace(/high\s*school|school|jr\.?\s*college|junior\s*college|college|inter|intermediate/g, "")
        .replace(/[^a-z0-9]/g, "")
        .trim();
    };

    // 1. School Match (High Priority)
    const cSchool = (candidate.school_name || "").trim();
    const mSchool = (me.school_name || "").trim();
    const normCSchool = normSchoolStr(cSchool);
    const normMSchool = normSchoolStr(mSchool);
    if (normMSchool && normCSchool && (normMSchool === normCSchool || normMSchool.includes(normCSchool) || normCSchool.includes(normMSchool))) {
      score += 50;
      reasons.push(`🏫 Same School: ${cSchool || mSchool}`);
    }

    // 2. Junior College / Intermediate Match
    const cCollege = (candidate.junior_college || "").trim();
    const mCollege = (me.junior_college || "").trim();
    const normCColl = normSchoolStr(cCollege);
    const normMColl = normSchoolStr(mCollege);
    if (normMColl && normCColl && (normMColl === normCColl || normMColl.includes(normCColl) || normCColl.includes(normMColl))) {
      score += 40;
      reasons.push(`🏫 Same College: ${cCollege || mCollege}`);
    }

    // 3. Class / Dept / Year Match
    if (mDept && cDept && mDept === cDept) {
      if (mYear && cYear && mYear === cYear) {
        if (mSec && cSec && mSec === cSec) {
          score += 45;
          reasons.push(`🎓 Same Class (${candidate.department} ${candidate.section || ''})`);
        } else {
          score += 35;
          reasons.push(`📚 Same Dept & Year (${candidate.department} ${candidate.year})`);
        }
      } else {
        score += 20;
        reasons.push(`📚 Same Dept (${candidate.department})`);
      }
    } else if (mYear && cYear && mYear === cYear) {
      score += 15;
      reasons.push(`🎓 Same Year (${candidate.year})`);
    }

    // 4. Hobbies Overlap
    const myH = parseListStr(me.hobbies);
    const candH = parseListStr(candidate.hobbies);
    const hobbyOverlap = myH.filter(x => candH.includes(x));
    if (hobbyOverlap.length > 0) {
      score += hobbyOverlap.length * 10;
      const hobbyNames = hobbyOverlap.slice(0, 2).map(h => h.charAt(0).toUpperCase() + h.slice(1)).join(', ');
      reasons.push(`🎨 Shared Hobbies (${hobbyNames})`);
    }

    // 5. Aspirations Overlap
    const myA = parseListStr(me.aspirations);
    const candA = parseListStr(candidate.aspirations);
    const aspOverlap = myA.filter(x => candA.includes(x));
    if (aspOverlap.length > 0) {
      score += aspOverlap.length * 8;
      const aspNames = aspOverlap.slice(0, 2).map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ');
      reasons.push(`🚀 Shared Aspirations (${aspNames})`);
    }

    // 6. Locality / City Match
    const cCity = (candidate.city || candidate.current_locality || "").trim().toLowerCase();
    const mCity = (me.city || me.current_locality || "").trim().toLowerCase();
    if (mCity && cCity && mCity === cCity) {
      score += 15;
      reasons.push(`📍 Same Locality (${candidate.city || candidate.current_locality})`);
    }

    // 7. Event-based participation check
    let isEvent = false;
    if (candidate.has_event_profile || candidate.event_registered || candidate.activities?.length > 0) {
      isEvent = true;
      score += 60; // Event-based matches boosted to top
      reasons.unshift(`⚡ Event Match (Convergence 2K26)`);
    }

    // Minor photo tiebreaker
    if (candidate.profile_pic) {
      score += 5;
    }

    return { score, reasons, isEvent };
  };

  const suggestions = useMemo(() => {
    if (!students || students.length === 0) return [];
    const me = user || {};
    const candidates = students.filter(s => (s.email || '').toLowerCase() !== (me.email || '').toLowerCase());

    const scored = candidates
      .map(c => {
        const { score, reasons, isEvent } = computeCandidateScore(c, me);
        return {
          ...c,
          _score: score,
          _reasons: reasons,
          _reason: reasons[0] || null,
          _isEvent: isEvent
        };
      })
      .filter(c => c._reasons && c._reasons.length > 0); // Must have at least one valid match reason

    // Event-based suggestions on top, People like you below
    const eventSuggestions = scored.filter(c => c._isEvent).sort((a, b) => b._score - a._score);
    const peopleLikeYou = scored.filter(c => !c._isEvent).sort((a, b) => b._score - a._score);

    return [...eventSuggestions, ...peopleLikeYou].slice(0, 15);
  }, [students, user]);

  // --- Handlers ---

  const handleLogout = () => {
    localStorage.removeItem("vjconnect_user");
    navigate("/");
  };

  const handleProfileOpen = (email) => {
    if (!email) return;
    const rollNo = email.split('@')[0];
    navigate(`/${rollNo}`);
    setNotifOpen(false);
    setSearchTerm("");
  };

  const handleAccept = async (notif) => {
    try {
      const res = await fetch(`${API_URL}/connect/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accepterEmail: user.email,
          accepterName: user.name,
          requesterEmail: notif.sender_email
        })
      });
      if (res.ok) {
        // Mark as read
        await fetch(`${API_URL}/notifications/mark-read-one`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: notif.id })
        });

        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, acceptedNow: true } : n));
        const checkRes = await fetch(`${API_URL}/student/profile?email=${notif.sender_email}&requesterEmail=${user.email}`);
        const checkData = await checkRes.json();
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, needsConnectBack: checkData.sentStatus === 'none' } : n));
        alert(`You are now connected with ${notif.sender_name}!`);
      }
    } catch (err) { console.error("Accept error", err); }
  };

  const handleReject = async (notif) => {
    try {
      const res = await fetch(`${API_URL}/connect/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientEmail: user.email, requesterEmail: notif.sender_email })
      });
      if (res.ok) {
        // Mark as read
        await fetch(`${API_URL}/notifications/mark-read-one`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: notif.id })
        });
        fetchNotifs();
      }
    } catch (err) { console.error("Reject error", err); }
  };

  const handleMarkRead = () => {
    fetch(`${API_URL}/notifications/mark-read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: user.email })
    }).then(() => {
      setNotifications([]);
      setNotifOpen(false);
    });
  };

  const handleConnectFromNotif = async (notif) => {
    try {
      const res = await fetch(`${API_URL}/connect/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senderEmail: user.email, senderName: user.name, recipientEmail: notif.sender_email })
      });
      if (res.ok) {
        // Mark notification as read on backend
        await fetch(`${API_URL}/notifications/mark-read-one`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: notif.id })
        });

        alert("Connection request sent!");
        // Remove locally
        setNotifications(prev => prev.filter(n => n.id !== notif.id));
        fetchNotifs();
      }
    } catch (err) { console.error("Connect error", err); }
  };

  // Calculate schedule preview text for bento tile
  const schedulePreviewText = useMemo(() => {
    if (loadingSchedule) return 'Loading schedule...';
    if (todaySchedule.length === 0) return currentWeekday === 'Sunday' ? '☀️ Sunday rest day' : 'No classes scheduled';

    const now = currentTime;
    let current = null;
    let next = null;
    const sorted = [...todaySchedule].sort((a, b) => a.start_time.localeCompare(b.start_time));
    for (const block of sorted) {
      try {
        const [sH, sM] = block.start_time.split(':').map(Number);
        const [eH, eM] = block.end_time.split(':').map(Number);
        const start = new Date(now).setHours(sH, sM, 0, 0);
        const end = new Date(now).setHours(eH, eM, 0, 0);
        const timeVal = now.getTime();
        if (timeVal >= start && timeVal <= end) current = block;
        else if (timeVal < start && !next) next = block;
      } catch {}
    }

    if (current) return `🟢 NOW: ${current.subject || 'Lecture'} (${current.start_time}-${current.end_time}) ${current.room_no ? `@ ${current.room_no}` : (defaultRoom ? `@ Room ${defaultRoom}` : '')}`;
    if (next) return `⏳ NEXT: ${next.subject || 'Lecture'} at ${next.start_time} ${next.room_no ? `@ ${next.room_no}` : (defaultRoom ? `@ Room ${defaultRoom}` : '')}`;
    return 'All classes completed today';
  }, [loadingSchedule, todaySchedule, currentWeekday, currentTime, defaultRoom]);

  const currentFeatures = getFeatures(user.role, formationTasks, () => setShowScheduleModal(true), schedulePreviewText);

  // --- Animation Variants ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <div className="home-page-container">
      <div className="home-content-wrapper">

        {/* Push Notification Banner */}
        {showPushPrompt && (
          <div style={{ background: 'var(--accent-color)', color: 'white', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div>
              <strong>Enable Push Notifications</strong>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>Get real-time updates for attendance & team invitations.</p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleDismissPush} style={{ background: 'transparent', border: '1px solid white', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer' }}>Later</button>
              <button onClick={handleEnablePush} style={{ background: 'white', border: 'none', color: 'var(--accent-color)', padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Enable</button>
            </div>
          </div>
        )}

        {/* === Header === */}
        <header className="home-header">
          <div className="brand-section">
            <h1 className="brand-title">VJ Link</h1>
            <span className="greeting-text">
              {(() => {
                const h = new Date().getHours();
                if (h < 12) return "Good Morning";
                if (h < 18) return "Good Afternoon";
                return "Good Evening";
              })()}, {user.name ? user.name : 'Scholar'}
            </span>
          </div>

          <div className="header-actions">
            {/* Theme Toggle */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="icon-btn"
              onClick={toggleTheme}
              title={theme === 'dark' ? "Switch to Bright Mode" : "Switch to Dark Mode"}
            >
              {theme === 'dark' ? <Sun size={20} className="text-amber-400" color="#fcd34d" /> : <Moon size={20} className="text-slate-600" color="#475569" />}
            </motion.button>
            {/* Notification Bell */}
            <div style={{ position: 'relative' }} ref={notifRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="icon-btn"
                onClick={() => setNotifOpen(!notifOpen)}
              >
                <Bell size={20} />
                {notifications.length > 0 && <span className="notification-badge" />}
              </motion.button>

              <AnimatePresence>
                {notifOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="notifications-dropdown"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--glass-border)', paddingBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>Notifications</span>
                      {notifications.length > 0 && (
                        <button onClick={handleMarkRead} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', fontSize: '0.8rem', cursor: 'pointer' }}>
                          Clear All
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No new notifications</div>
                    ) : (
                      <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {notifications.map(n => (
                          <div key={n.id} className="notif-item"
                            onClick={async () => {
                              if (n.link) {
                                // Mark as read when clicked
                                await fetch(`${API_URL}/notifications/mark-read-one`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ id: n.id })
                                });
                                // Update local state
                                setNotifications(prev => prev.filter(item => item.id !== n.id));
                                navigate(n.link);
                              }
                            }}
                            style={{ cursor: n.link ? 'pointer' : 'default' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.9rem' }}>
                                {n.title || n.sender_name}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                {n.message || (n.type === 'connection_request' ? 'sent you a connection request' : 'accepted your request')}
                              </div>
                            </div>

                            {/* Team Invitation Actions */}
                            {n.type === 'team_invitation' && (
                              <div style={{ display: 'flex', gap: '0.5rem' }} onClick={(e) => e.stopPropagation()}>
                                <button className="btn-accept" onClick={() => handleInvitationResponse(n.id, true)}>
                                  <Check size={14} /> Accept
                                </button>
                                <button className="btn-accept" style={{ background: '#ef4444' }} onClick={() => handleInvitationResponse(n.id, false)}>
                                  <X size={14} /> Reject
                                </button>
                              </div>
                            )}

                            {/* Connection Request Actions (existing) */}
                            {n.type === 'connection_request' && !n.acceptedNow && (
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button className="btn-accept" onClick={() => handleAccept(n)}><Check size={14} /></button>
                                <button className="btn-accept" style={{ background: '#ef4444' }} onClick={() => handleReject(n)}><X size={14} /></button>
                              </div>
                            )}

                            {/* Event Connection Request Actions */}
                            {n.type === 'event_connection_request' && (
                              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <button className="btn-accept" title="Accept Event Connection" onClick={() => handleEventConnectionResponse(n, true)}>
                                  <Check size={14} /> Accept
                                </button>
                                <button className="btn-accept" style={{ background: '#ef4444' }} title="Reject" onClick={() => handleEventConnectionResponse(n, false)}>
                                  <X size={14} />
                                </button>
                              </div>
                            )}

                            {/* Case 2: Just Accepted (Client Side) OR Connect Back Pending (Server Side) */}
                            {((n.acceptedNow && n.needsConnectBack) || n.type === 'connect_back_pending') && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.2rem' }}>
                                {n.acceptedNow && <span style={{ color: '#22c55e', fontSize: '0.7rem' }}>Accepted</span>}
                                <button
                                  className="btn-accept"
                                  style={{ background: 'var(--accent-color)', color: 'var(--text-main)', fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}
                                  onClick={(e) => { e.stopPropagation(); handleConnectFromNotif(n); }}
                                >
                                  Connect Back <ExternalLink size={10} style={{ marginLeft: 3 }} />
                                </button>
                              </div>
                            )}

                            {/* Case 3: Accepted (No action needed) */}
                            {n.acceptedNow && !n.needsConnectBack && <span style={{ color: '#22c55e', fontSize: '0.8rem' }}>Accepted</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Hamburger Menu */}
            <div style={{ position: 'relative' }} ref={menuRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="icon-btn"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <Menu size={20} />
              </motion.button>

              <AnimatePresence>
                {menuOpen && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, x: 10 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.95, x: 10 }}
                    className="notifications-dropdown"
                    style={{ width: '200px' }}
                  >
                    <button className="search-result-row" onClick={() => { setMenuOpen(false); handleProfileOpen(user.email); }}>
                      <User size={18} /> Profile
                    </button>
                    {(() => {
                      const stored = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
                      const email = (stored.email || '').toString().trim();
                      const adminEmail = "24071a6691@vnrvjiet.in";
                      const coAdminEmail = "24071a6679@vnrvjiet.in";

                      if (email === adminEmail || email === coAdminEmail) {
                        const label = email === coAdminEmail ? "Co-Admin" : "Admin";
                        return (
                          <button className="search-result-row" onClick={() => { setMenuOpen(false); navigate('/admin'); }}>
                            <Sparkles size={18} /> {label}
                          </button>
                        );
                      }
                    })()}
                    <div style={{ height: '1px', background: 'var(--glass-border)', margin: '0.5rem 0' }} />
                    <button className="search-result-row" onClick={() => { setMenuOpen(false); handleLogout(); }}>
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* === Search Section === */}
        <section className="search-section">
          <div className="search-pill-container">
            <div className="search-input-group">
              <Search className="search-icon" size={24} />
              <input
                type="text"
                className="search-input-modern"
                placeholder="Search students, roll numbers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
              />
            </div>

            <AnimatePresence>
              {(searchFocused || searchTerm) && filteredStudents.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="search-results-glass"
                >
                  {filteredStudents.length === 0 ? (
                    <div className="no-results-text">No such user found</div>
                  ) : (
                    filteredStudents.slice(0, 8).map(student => (
                      <div key={student.email} className="search-result-row" onClick={() => handleProfileOpen(student.email)}>
                        <div className="result-avatar-small">
                          {student.profile_pic ? (
                            <img src={student.profile_pic} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (student.name || '?').charAt(0)}
                        </div>
                        <div className="result-info">
                          <div className="result-name">
                            {student.name}
                            {isGoldVerified(student) && <GoldVerifiedBadge size={14} style={{ display: 'inline', marginLeft: 4 }} />}
                          </div>
                          <div className="result-meta">{student.rollNo} • {student.department}</div>
                        </div>
                      </div>
                    ))
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <motion.main
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}
        >
          <AnnouncementsBanner user={user} />

              {/* Detailed Schedule Modal */}
              <AnimatePresence>
                {showScheduleModal && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(8px)' }}
                    onClick={() => setShowScheduleModal(false)}>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 20 }}
                      className="glass-card" 
                      onClick={e => e.stopPropagation()}
                      style={{ padding: '2rem', maxWidth: '500px', width: '92%', maxHeight: '85vh', overflowY: 'auto', position: 'relative', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)' }}
                    >
                      <button onClick={() => setShowScheduleModal(false)} style={{ position: 'absolute', top: '1.2rem', right: '1.2rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                        <X size={22} />
                      </button>
                      <h2 style={{ fontSize: '1.4rem', margin: '0 0 0.2rem 0', fontWeight: 800 }}>📅 Class Schedule</h2>
                      <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                        Viewing {selectedDay}'s schedule {defaultRoom && `· Default Room: ${defaultRoom}`}
                      </p>

                      {/* Day selection tabs */}
                      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.4rem' }}>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(d => (
                          <button key={d} type="button" onClick={() => fetchScheduleForDay(d)}
                            style={{
                              padding: '0.4rem 0.8rem',
                              borderRadius: '20px',
                              border: '1px solid var(--glass-border)',
                              background: selectedDay === d ? 'var(--accent-color)' : 'var(--glass-bg)',
                              color: selectedDay === d ? 'white' : 'var(--text-main)',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s'
                            }}
                          >
                            {d.slice(0, 3)}
                          </button>
                        ))}
                      </div>

                      {modalSchedule.length === 0 ? (
                        <div style={{ color: 'var(--text-muted)', padding: '2rem 1rem', textAlign: 'center' }}>
                          🎉 No classes scheduled for {selectedDay}.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                          {modalSchedule.map(block => {
                            const checkIsActive = () => {
                              try {
                                const now = currentTime;
                                const [sH, sM] = block.start_time.split(':').map(Number);
                                const [eH, eM] = block.end_time.split(':').map(Number);
                                const start = new Date(now).setHours(sH, sM, 0, 0);
                                const end = new Date(now).setHours(eH, eM, 0, 0);
                                return now.getTime() >= start && now.getTime() <= end;
                              } catch {
                                return false;
                              }
                            };

                            const isActive = checkIsActive();

                            return (
                              <div key={block.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '1rem',
                                  padding: '1rem',
                                  borderRadius: '12px',
                                  background: isActive ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.02)',
                                  border: isActive ? '1.5px solid var(--accent-color)' : '1px solid var(--glass-border)',
                                  boxShadow: isActive ? '0 0 12px rgba(99,102,241,0.15)' : 'none',
                                }}
                              >
                                <div style={{ minWidth: '90px', display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '0.88rem', fontWeight: 'bold', color: isActive ? 'var(--accent-color)' : 'var(--text-main)' }}>
                                    {block.start_time}
                                  </span>
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    Period {block.period_number}
                                  </span>
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--text-main)' }}>
                                      {block.subject || 'Unspecified'}
                                    </span>
                                    <span style={{
                                      fontSize: '0.7rem',
                                      padding: '0.15rem 0.4rem',
                                      borderRadius: '12px',
                                      fontWeight: 'bold',
                                      background: block.block_type === 'lab' ? 'rgba(139,92,246,0.15)' : 'rgba(99,102,241,0.15)',
                                      color: block.block_type === 'lab' ? '#a78bfa' : 'var(--accent-color)'
                                    }}>
                                      {block.block_type === 'lab' ? 'Lab' : 'Lecture'}
                                    </span>
                                    {(block.room_no || defaultRoom) && (
                                      <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)' }}>
                                        🚪 {block.room_no || defaultRoom}
                                      </span>
                                    )}
                                    {isActive && (
                                      <span style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem', borderRadius: '12px', background: '#ef4444', color: 'white', fontWeight: 'bold' }}>
                                        NOW
                                      </span>
                                    )}
                                  </div>
                                  {(!block.is_temporary && block.faculty_email && block.faculty_email !== 'placeholder@vjlink.in') ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                      <span>Teacher:</span>
                                      <Link to={`/${block.faculty_email.split('@')[0]}`} onClick={() => setShowScheduleModal(false)} style={{ color: 'var(--accent-color)', textDecoration: 'none', fontWeight: '500' }}>
                                        {block.faculty_name || block.faculty_email}
                                      </Link>
                                    </div>
                                  ) : (
                                    <div style={{ marginTop: '0.2rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                      {block.is_temporary ? '⏳ Temporary Placeholder' : '—'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>

          {/* === Feature Tiles (Bento Grid) === */}
          <motion.section variants={itemVariants} className="features-grid-bento">
            {currentFeatures.map((feature, idx) => {
              const content = (
                <>
                  <div className="tile-icon-box">
                    {feature.name === "Profile" && user.profile_pic ? (
                      <img src={user.profile_pic} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 24 }} />
                    ) : (
                      React.cloneElement(feature.icon, { color: 'currentColor' })
                    )}
                  </div>
                  <div className="tile-content">
                    <div className="tile-title">{feature.name}</div>
                    <div className="tile-desc">{feature.desc}</div>
                  </div>
                  <div className="feature-tile-bg-circle" />
                </>
              );

              if (feature.onClick) {
                return (
                  <div key={feature.name} onClick={feature.onClick} className="feature-tile" style={{ cursor: 'pointer' }}>
                    {content}
                  </div>
                );
              }

              return (
                <Link to={feature.link} key={feature.name} className="feature-tile">
                  {content}
                </Link>
              );
            })}
          </motion.section>

          {/* === Suggestions Carousel === */}
          {suggestions.length > 0 && (
            <motion.section variants={itemVariants} className="suggestions-section">
              <div className="section-title">
                <Sparkles size={18} className="text-yellow-400" />
                <span>People You Might Know</span>
              </div>

              <SuggestionsCarousel suggestions={suggestions} handleProfileOpen={handleProfileOpen} />
            </motion.section>
          )}
        </motion.main>
      </div>
    </div>
  );
}